// Real-company branding for Classification design 5. Used by:
//   - edit.js at level-create time: convert the source template name
//     (e.g. 'Apple (Realco)') into the new level's name
//     (e.g. 'real-public-co-Apple').
//   - multistep.js at runtime: convert the level's name back into the
//     display name + logo for the center header.
// Add a new entry here when the backend ships a new realco template.

import appleLogo from './images/realcos/apple.png';
import costcoLogo from './images/realcos/costco.png';
import fedexLogo from './images/realcos/fedex.png';
import googleLogo from './images/realcos/google.png';
import chaseLogo from './images/realcos/chase.png';
import marriottLogo from './images/realcos/marriott.png';
import mcdonaldsLogo from './images/realcos/mcdonalds.png';
import netflixLogo from './images/realcos/netflix.png';
import nikeLogo from './images/realcos/nike.png';
import starbucksLogo from './images/realcos/starbucks.png';
import disneyLogo from './images/realcos/disney.png';
import warnerMusicLogo from './images/realcos/warner-music.png';

// Logo by display name. Display names match what the instructor sees
// in the modal — the source template's `(Realco)` suffix is stripped
// before lookup, so e.g. "Apple (Realco)" → "Apple" → appleLogo.
export const realcoLogos = {
  'Apple':         appleLogo,
  'Costco':        costcoLogo,
  'FedEx':         fedexLogo,
  'Google':        googleLogo,
  'Chase':         chaseLogo,
  'Marriott':      marriottLogo,
  "McDonald's":    mcdonaldsLogo,
  'NetFlix':       netflixLogo,
  'Nike':          nikeLogo,
  'Starbucks':     starbucksLogo,
  'Disney':        disneyLogo,
  'Warner Music':  warnerMusicLogo,
};

export const REALCO_LEVEL_PREFIX = 'real-public-co-';

// Title-case "warner music" → "Warner Music", "apple" → "Apple".
const titleCase = (s) =>
  s.replace(/\b\w/g, c => c.toUpperCase());

// Recover the company's display name from however the backend stores
// the template name. Handles every variant we've seen so far:
//   "Apple (Realco)"        → "Apple"
//   "apple-(realco)"        → "Apple"
//   "apple-realco"          → "Apple"
//   "Apple Realco"          → "Apple"
//   "warner-music-realco"   → "Warner Music"
//   "Marriott"              → "Marriott" (no realco suffix, no-op)
const extractRealcoDisplayName = (templateName) => {
  if (!templateName) return '';
  // 1. Strip trailing parenthesized content (e.g. "(Realco)").
  let cleaned = templateName.replace(/\s*\([^)]*\)\s*$/, '').trim();
  // 2. Strip a trailing "realco" / "-realco" / "_realco" / " realco"
  //    (case-insensitive) so kebab/space-separated names work too.
  cleaned = cleaned.replace(/[\s\-_]*realco\s*$/i, '').trim();
  // 3. Strip any trailing separator left from kebab/snake forms.
  cleaned = cleaned.replace(/[\-_\s]+$/, '').trim();
  // 4. Normalize kebab/snake into spaces, then title-case so the
  //    final value matches our realcoLogos keys (e.g. "Warner Music").
  cleaned = cleaned.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned ? titleCase(cleaned) : '';
};

// Runtime lookup: the multistep board passes the level name (e.g.
// 'real-public-co-Apple') and gets back { displayName, logo }.
export const getRealcoCompany = (scenarioLevelName) => {
  if (!scenarioLevelName) return null;
  if (!scenarioLevelName.startsWith(REALCO_LEVEL_PREFIX)) return null;
  const displayName = scenarioLevelName.slice(REALCO_LEVEL_PREFIX.length);
  const logo = realcoLogos[displayName] || null;
  return { displayName, logo };
};

// Create-time helper: edit.js passes the source template's name (e.g.
// 'Apple (Realco)') and gets back the new level's name
// (e.g. 'real-public-co-Apple'). Returns null when no display name can
// be extracted so the caller falls back to the decision-type key.
export const buildRealcoLevelName = (templateName) => {
  const displayName = extractRealcoDisplayName(templateName);
  return displayName ? `${REALCO_LEVEL_PREFIX}${displayName}` : null;
};
